/**
 * Privacy policy — one PAGE, one body per brand.
 *
 * This file owns the CHROME only: the scroll container, the header that offers
 * Back or a wordmark depending on how the reader arrived, the contents nav, the
 * contact block, and the theming. It makes no factual claim about the product.
 *
 * THE CLAIMS LIVE IN `./privacy/<brand>.tsx`, each beside an audit note saying
 * which code was read for it. That is deliberate: this docblock used to carry
 * the evidence list, and when the claims were corrected it went on describing
 * an IP-salt mechanism and a 90-day deletion that had just been removed for
 * being untrue. Evidence kept away from the sentence it supports goes stale
 * silently, and a stale audit note is worse than none because it is believed.
 *
 * NOT LEGAL ADVICE AND NOT REVIEWED BY COUNSEL.
 */
import { useEffect, useLayoutEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTheme, getThemeClass } from '@papan/core-ui/ui'
import { useBrand, usePageTitle } from '@papan/brand/react'
import { policyFor } from './privacy'



/**
 * The contact section is not built by a brand (its body needs the entity facts,
 * not a builder), so it is named once here and joined onto the contents.
 */
const CONTACT_ENTRY = { id: 'contact', heading: 'How to contact us' } as const


export function PrivacyPage() {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const { brand, productName } = useBrand()
  const navigate = useNavigate()
  const location = useLocation()
  /**
   * Did the reader ARRIVE here from somewhere inside the app?
   *
   * `location.key` is the router's per-entry id and is the string 'default'
   * only for the FIRST entry in a history stack. So this distinguishes the two
   * ways this page is reached, which want opposite affordances:
   *
   *   · in-app navigation (a footer or settings link)  ->  go BACK, to whatever
   *     they were reading. A hardcoded link to "/" threw that away and dropped
   *     them on the app home, which is what was reported.
   *
   *   · a fresh tab  ->  there is nothing behind this page. The share gate
   *     links here with target="_blank", so this is the COMMON case, not the
   *     edge one: `navigate(-1)` in a tab with a one-entry stack does nothing
   *     at all, leaving a Back control that silently no-ops. Offer the product
   *     instead, and say so.
   *
   * `history.length` would be the obvious test and is the wrong one: it counts
   * forward entries too, is capped by the browser, and is shared with pages the
   * router never saw. Measured in this app, a fresh tab already reports 2.
   *
   * Measured signals, for whoever revisits this:
   *   fresh entry            history.state = {idx: 0}                 key 'default'
   *   after a router <Link>  history.state = {usr, key, idx: 1}       key set
   * so `key !== 'default'` and `state.idx > 0` agree. The key is used because
   * it is the router's own reactive API rather than raw history access.
   *
   * NOTE for testing this by hand: a raw `history.pushState` does NOT set it.
   * React Router only stamps entries it creates, so simulating navigation that
   * way reports 'default' and makes a working control look broken.
   */
  const cameFromInApp = location.key !== 'default'

  // The legal entity, falling back to the product name — the same fallback
  // `identity.companyName`'s own docblock describes for every other render
  // site, so a brand that never set one reads consistently everywhere.
  const company = brand.identity.companyName ?? productName
  // Papan's own `companyName` is deliberately unset (see DEFAULT_BRAND), so the
  // fallback makes company === productName and "X is operated by X" reads as a
  // bug. Only name a separate operator when there IS one.
  const operatorIsSeparate = company !== productName
  const { entityAddress, registrationNumber, contactEmail } = brand.legal

  // The TEXT is per brand, the CHROME below is not. See ./privacy/types.ts.
  // The date belongs to the BODY, not the page: one shared constant meant
  // editing one brand's text restamped every other brand's policy as freshly
  // reviewed, which is a claim about a document nobody read.
  const { build, lastUpdated } = policyFor(brand.brandKey)
  const sections = build({ productName, company, contactEmail, operatorIsSeparate })
  /**
   * Contents entries, INCLUDING the contact section.
   *
   * The contact block is rendered outside `sections` because its body needs the
   * entity facts rather than a builder, and the nav used to map `sections`
   * alone. So the single destination a reader most often arrives looking for,
   * "how do I actually ask you for my data", was the one item missing from the
   * list of destinations. Both the nav and the section numbering read this, so
   * they cannot drift apart again.
   */
  const contents = [...sections.map(({ id, heading }) => ({ id, heading })), CONTACT_ENTRY]

  usePageTitle('Privacy Policy')

  /**
   * The application shell deliberately locks document scrolling because its
   * workspaces own their own scroll regions. A public legal document is the
   * opposite: it should participate in ordinary browser flow, work with page
   * search, and print without an overflow viewport clipping it.
   *
   * Scope the exception to this route and restore the shell contract on
   * unmount. `useLayoutEffect` applies it before paint, so the page does not
   * briefly render as a clipped app panel.
   */
  useLayoutEffect(() => {
    document.documentElement.classList.add('privacy-document')
    return () => document.documentElement.classList.remove('privacy-document')
  }, [])

  /**
   * The operator instruction the page used to print at readers.
   *
   * A console line rather than page copy: it names config only we can change,
   * so it belongs where the person who can change it will see it — and it keeps
   * a missing legal contact from being something you have to know to look for.
   */
  useEffect(() => {
    const missing = [
      !contactEmail && 'legal.contactEmail',
      !entityAddress && 'legal.entityAddress',
      !registrationNumber && 'legal.registrationNumber',
    ].filter(Boolean)
    if (!missing.length) return
    console.warn(
      `[privacy] Policy is rendering as a DRAFT: ${missing.join(', ')} unset in ` +
      `config/brands/<key>/brand.yaml. A policy with no contact address cannot ` +
      `satisfy a PDPA access or correction request. Set them, and have the text ` +
      `reviewed, before this is treated as published.`,
    )
  }, [contactEmail, entityAddress, registrationNumber])

  return (
    <div className={`${themeClass} privacy-page themed-bg-primary min-h-screen`}>
      <div className="mx-auto w-full max-w-3xl px-5 py-10 md:px-8 md:py-16">
        <header className="mb-10">
          {cameFromInApp
            ? (
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="privacy-page-navigation -ml-1 inline-flex min-h-[44px] items-center px-1 text-sm themed-text-secondary hover:themed-text transition-colors"
              >
                &larr; Back
              </button>
            )
            : (
              /* NO back arrow here, and that is the fix rather than a detail.
                 The dominant way this page is opened is the share gate's
                 target="_blank" link: a reader part-way through entering their
                 email clicks it to find out what happens to that address, and
                 lands in a tab with an empty history. An arrow reads as "back",
                 so following it and arriving on the app home felt like being
                 thrown out of what they were doing — which is exactly what it
                 was, since "/" abandons the share they came to open and may
                 not even be somewhere they have an account.

                 A plain wordmark instead: it says whose policy this is, which
                 is what a legal page's masthead is for, and offers the product
                 to a reader who arrived from a search or a bookmark without
                 pretending to be a way back. The way back from this tab is to
                 close it, and `window.close()` cannot do that for a tab the
                 page did not open, so there is no honest button for it. */
              <Link
                to="/"
                className="privacy-page-navigation -ml-1 inline-flex min-h-[44px] items-center px-1 text-sm font-medium themed-text-secondary hover:themed-text transition-colors"
              >
                {productName}
              </Link>
            )}
          <h1 className="mt-4 text-3xl md:text-4xl font-semibold themed-text">
            Privacy Policy
          </h1>
          <p className="mt-2 text-sm themed-text-secondary">
            {/* The operator is named here only when it is NOT the wordmark
                directly above. For a brand with no separate legal entity the
                fallback makes them the same string, so the byline read
                "Papan / Papan / Last updated ..." down the page. */}
            {operatorIsSeparate && <>{company} &middot; </>}Last updated {lastUpdated}
          </p>
        </header>

        {/* Contents. A privacy policy is read by someone looking for ONE
            answer — "do you send my files to an AI company", "how do I get my
            data deleted" — not front to back. */}
        <nav aria-label="Contents" className="privacy-page-contents mb-10 rounded-xl themed-bg-secondary p-5">
          <h2 className="text-xs uppercase tracking-wider themed-text-secondary font-medium mb-3">
            Contents
          </h2>
          <ol className="space-y-0.5">
            {contents.map((s, i) => (
              <li key={s.id}>
                {/* `block py-2` is the touch target. The contents list is the
                    primary way this document is navigated and a bare 20px line
                    of text is not something a thumb hits reliably. */}
                <a href={`#${s.id}`} className="flex min-h-[44px] items-center py-2 text-[15px] themed-text hover:underline">
                  <span className="themed-text-secondary tabular-nums mr-2">{i + 1}.</span>
                  {s.heading}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <main className="space-y-10">
          {sections.map((s, i) => (
            <section key={s.id} id={s.id} className="scroll-mt-8">
              <h2 className="text-xl font-semibold themed-text mb-3">
                <span className="themed-text-secondary tabular-nums mr-2">{i + 1}.</span>
                {s.heading}
              </h2>
              <div className="space-y-3 text-base md:text-[15px] leading-relaxed themed-text-secondary">
                {s.body}
              </div>
            </section>
          ))}

          <section id="contact" className="scroll-mt-8">
            <h2 className="text-xl font-semibold themed-text mb-3">
              <span className="themed-text-secondary tabular-nums mr-2">{contents.length}.</span>
              How to contact us
            </h2>
            <div className="space-y-3 text-base md:text-[15px] leading-relaxed themed-text-secondary">
              <p>
                To make an access, correction or withdrawal request, or to ask anything
                about this policy, contact {company}:
              </p>
              <address className="not-italic rounded-xl themed-bg-secondary p-5 space-y-1">
                <div className="themed-text font-medium">{company}</div>
                {registrationNumber && (
                  <div>Company No. {registrationNumber}</div>
                )}
                {/* Newlines are how an address is written; preserve them rather
                    than collapsing a postal address onto one line. */}
                {entityAddress && (
                  <div className="whitespace-pre-line">{entityAddress}</div>
                )}
                {contactEmail && (
                  <div>
                    <a href={`mailto:${contactEmail}`} className="themed-text hover:underline">
                      {contactEmail}
                    </a>
                  </div>
                )}
                {/* Honest about the gap without printing a defect notice at a
                    reader who can do nothing about it, and without inventing an
                    address — a privacy contact that bounces is worse than one
                    that is openly not yet published. */}
                {(!contactEmail || !entityAddress) && (
                  <div className="themed-text-secondary pt-1">
                    Full contact details will be published here before general release.
                    In the meantime, please reach us through your organisation&rsquo;s
                    administrator.
                  </div>
                )}
              </address>
            </div>
          </section>
        </main>

        <footer className="mt-14 pt-6 border-t themed-border text-sm themed-text-secondary">
          <p>
            We may update this policy. The date at the top is the last substantive
            change, and material changes will be notified through {productName} or
            by email before they take effect.
          </p>
        </footer>
      </div>
    </div>
  )
}
